import { type ReactNode, useCallback, useState } from "react";
import { toast } from "sonner";
import { Dialog } from "../Dialog/Dialog";
import { DialogResult } from "../Dialog/DialogResult";
import {
  FormValidationActiveProvider,
  FormValidationSubmittedProvider,
  useIsFormSubmitted,
} from "./Internal/FormValidationContext";
import type { FormValidation } from "../Hooks/useFormValidation";
import type { DialogBaseProps } from "../Dialog/DialogBaseProps";

interface FormProps<T> extends DialogBaseProps {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  warning?: string;
  width?:
    | "narrow"
    | "default"
    | "medium"
    | "wide"
    | "extra-wide"
    | "full"
    | "auto";
  cols?: 1 | 2 | 3;
  /** Branded validation from useFormValidation. Form calls validate() before onSave. Use noValidation for forms without field validation. */
  validation: FormValidation<T>;
  onSave?: (
    validatedForm: Required<T>,
  ) => void | DialogResult | Promise<void | DialogResult>;
  /** Success toast shown after onSave completes. Errors are caught and shown automatically. */
  successMessage?: string;
  saveText?: string;
  cancelText?: string;
  saveLoading?: boolean;
  /**
   * Called when `onSave` throws. Default behavior is a generic toast; consumers
   * (e.g. Hubble) can provide a richer error renderer here.
   */
  onError?: (error: unknown) => void | Promise<void>;
  headerActions?: ReactNode;
  menu?: ReactNode;
  hideCloseButton?: boolean;
  /** When true, the Save button is enabled even if the user hasn't touched
   *  any control. Use for "approve / confirm proposal" dialogs that open
   *  prefilled with the payload to save. The "Discard changes?" prompt on
   *  close is unaffected — it still only fires if the user actually edited
   *  something. Defaults to false (Form's default pristine guard). */
  allowPristineSave?: boolean;
}

function optionalDialogProps({
  title,
  description,
  warning,
  width,
  hideCloseButton,
}: {
  title: ReactNode | undefined;
  description: ReactNode | undefined;
  warning: string | undefined;
  width: FormProps<unknown>["width"];
  hideCloseButton: boolean | undefined;
}) {
  return {
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...(warning ? { warning } : {}),
    ...(width ? { width } : {}),
    ...(hideCloseButton !== undefined ? { hideCloseButton } : {}),
  };
}

export function Form<T>({
  open,
  onOpenChange,
  children,
  title,
  description,
  warning,
  width,
  cols = 1,
  validation,
  onSave,
  onError,
  successMessage,
  saveText = "Save",
  cancelText = "Cancel",
  saveLoading = false,
  headerActions,
  menu,
  hideCloseButton,
  allowPristineSave = false,
}: FormProps<T>) {
  const [isSaving, setIsSaving] = useState(false);

  // An enclosing ShowFormErrors (stories/tests showcasing error states) sets
  // the outer submitted context; honor it so wrapped dialogs display their
  // errors immediately.
  const outerSubmitted = useIsFormSubmitted();

  // Validate then save. Auto-tracks loading state.
  // Catches errors and forwards to `onError` if provided (or a generic toast).
  // Shows successMessage toast on success.
  const handleSave = useCallback(async () => {
    if (!validation.validate()) return DialogResult.KeepOpen;
    if (!onSave) return;
    setIsSaving(true);
    let result: void | DialogResult;
    try {
      result = await onSave(validation.validatedForm);
    } catch (e) {
      if (onError) {
        await onError(e);
      } else {
        const message = e instanceof Error ? e.message : "Save failed";
        toast.error(message);
      }
      setIsSaving(false);
      return DialogResult.KeepOpen;
    }
    setIsSaving(false);
    if (result === DialogResult.KeepOpen) return DialogResult.KeepOpen;
    if (successMessage) toast.success(successMessage);
    return;
  }, [validation, onSave, onError, successMessage]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      {...optionalDialogProps({
        title,
        description,
        warning,
        width,
        hideCloseButton,
      })}
      headerActions={headerActions}
      menu={menu}
      {...(onSave ? { onOk: handleSave } : {})}
      okText={saveText}
      cancelText={cancelText}
      okLoading={saveLoading || isSaving}
      okDisabledIfPristine={!allowPristineSave}
      onCancel={() => {}}
    >
      <FormValidationActiveProvider value={true}>
        <FormValidationSubmittedProvider
          value={validation.submitted || outerSubmitted}
        >
          <div
            className={`grid ${cols === 3 ? "grid-cols-3" : cols === 2 ? "grid-cols-2" : "grid-cols-1"} gap-6`}
          >
            {children}
          </div>
        </FormValidationSubmittedProvider>
      </FormValidationActiveProvider>
    </Dialog>
  );
}
